import { useState } from "react";
import { useDispatch } from "react-redux";

import Modal from "components/Modal";
import Button from "components/Button";
import Message from "components/Message";
import Checkbox from "components/Checkbox";
import RuleRow from "./RuleRow";
import RuleSummary from "./RuleSummary";
import SortArrow from "../SortArrow/SortArrow";
import DeleteConfirmModal from "../DeleteConfirmModal";

import { deleteRfqNotificationRule } from "actions/rfqNotifications";
import { getApiErrorMessage } from "utils/apiErrors";

import styles from "./RulesList.module.scss";

// Columns that the server can sort by. Labels are rendered as clickable
// headers; the `field` is sent to the API as `sort_field`.
const SORTABLE_COLUMNS = [
  { field: "name", label: "Name" },
  { field: "created_by", label: "Created By" },
  { field: "created_time", label: "Created Time" },
  { field: "last_edited_by", label: "Last Edited By" },
  { field: "last_edited_time", label: "Last Edited Time" },
];

const RulesList = ({
  rules,
  onEdit,
  onDelete,
  onTest,
  loading,
  error,
  onCreate,
  sortField,
  sortDirection,
  onSort,
  count,
  offset,
  limit,
  onPageChange,
  nameQuery,
  onNameSearch,
  isActiveOnly,
  onActiveToggle,
  canFilterMine,
  showMine,
  onMineToggle,
}) => {
  const [nameInput, setNameInput] = useState(nameQuery || "");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const dispatch = useDispatch();

  const handleDelete = (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    setSelectedRule(rule);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (ruleId) => {
    setDeleteError(null);
    setDeleting(true);
    try {
      await dispatch(deleteRfqNotificationRule(ruleId));
      onDelete(ruleId);
      setShowDeleteModal(false);
      setSelectedRule(null);
    } catch (error) {
      console.error("Error deleting rule:", error);
      setDeleteError(getApiErrorMessage(error) || "Failed to delete notification rule");
    } finally {
      setDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedRule(null);
    setDeleteError(null);
  };

  const handleNameSearch = () => {
    onNameSearch(nameInput.trim());
  };

  const handleNameKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleNameSearch();
    }
  };

  // The list is filtered/sorted/paginated entirely on the server; `rules` is
  // already the current page of results, rendered as-is.
  const totalPages = Math.max(1, Math.ceil((count || 0) / (limit || 1)));
  const currentPage = Math.floor((offset || 0) / (limit || 1)) + 1;
  const canPrev = (offset || 0) > 0;
  const canNext = (offset || 0) + (limit || 0) < (count || 0);

  if (loading) {
    return <div className={styles.loading}>Loading rules...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  const renderHeaderColumn = ({ field, label }) => {
    const isActiveSort = sortField === field;
    return (
      <button
        key={field}
        type="button"
        className={`${styles.column} ${styles.sortable}`}
        onClick={() => onSort(field)}
      >
        {label}
        <SortArrow direction={isActiveSort ? sortDirection : null} />
      </button>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.topHeader}>
        <div className={styles.controls}>
          <div className={styles.nameSearch}>
            <input
              type="text"
              placeholder="Search by name, ID, recipient, or creator..."
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={handleNameKeyDown}
              className={styles.searchInput}
              aria-label="Search rules"
            />
            <Button onClick={handleNameSearch} className={styles.searchButton}>
              Search
            </Button>
          </div>
          <div className={styles.filterContainer}>
            <Checkbox
              id="show-active-only"
              value={isActiveOnly}
              onChange={(e) => onActiveToggle(e.target.checked)}
              label="Show active only"
            />
            {canFilterMine && (
              <Checkbox
                id="show-my-criterias"
                value={showMine}
                onChange={(e) => onMineToggle(e.target.checked)}
                label="Show My Criterias"
              />
            )}
          </div>
        </div>
        <Button onClick={onCreate} className={styles.createButton}>
          Create Rule
        </Button>
      </div>

      {rules.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No notification rules found.</p>
          <Button onClick={onCreate}>Create Your First Rule</Button>
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.header}>
            <div className={styles.row}>
              {renderHeaderColumn(SORTABLE_COLUMNS[0])}
              <div className={styles.column}>Criteria</div>
              <div className={styles.column}>Truck Types</div>
              <div className={styles.column}>Contacts</div>
              <div className={styles.column}>Status</div>
              {renderHeaderColumn(SORTABLE_COLUMNS[1])}
              {renderHeaderColumn(SORTABLE_COLUMNS[2])}
              {renderHeaderColumn(SORTABLE_COLUMNS[3])}
              {renderHeaderColumn(SORTABLE_COLUMNS[4])}
              <div className={styles.column}>Email Recipients</div>
            </div>
          </div>
          <div className={styles.body}>
            {rules.map((rule) => (
              <RuleRow
                key={rule.id}
                rule={rule}
                onEdit={onEdit}
                onDelete={handleDelete}
                onTest={onTest}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pager only when there's more than one page; Previous/Next are hidden
          (not just disabled) on the first/last page respectively. */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          {canPrev && (
            <Button
              onClick={() => onPageChange(Math.max(0, (offset || 0) - (limit || 0)))}
              className={styles.pageButton}
            >
              Previous
            </Button>
          )}
          <span className={styles.pageInfo}>
            Page {currentPage} of {totalPages} ({count} total)
          </span>
          {canNext && (
            <Button
              onClick={() => onPageChange((offset || 0) + (limit || 0))}
              className={styles.pageButton}
            >
              Next
            </Button>
          )}
        </div>
      )}

      {showDeleteModal && selectedRule && (
        <Modal
          isOpen={showDeleteModal}
          onClose={closeDeleteModal}
          hideCloseBtn={true}
          content={
            <DeleteConfirmModal
              isOpen={showDeleteModal}
              rule={selectedRule}
              onConfirm={handleDeleteConfirm}
              onClose={closeDeleteModal}
              loading={deleting}
              error={deleteError}
            />
          }
        />
      )}
    </div>
  );
};

export default RulesList;
